/**
 * Работа с моделью связей: создание и удаление relationship + view connection.
 * Не трогает граф X6 — только модель (moddle). Добавление/удаление edge на графе — через EdgeFactory.
 */
import {ARCHIMATE_RELATIONSHIP, ARCHIMATE_RELATIONSHIPS} from '../metamodel/Concept';

function getRefId(ref) {
	if (ref == null) return null;
	if (typeof ref === 'string') return ref;
	return ref.id ?? ref.$attrs?.id ?? null;
}

function relationshipsNodeOf(model) {
	return model?.relationshipsNode || model?.relationships || null;
}

function listRelationships(model) {
	const node = relationshipsNodeOf(model);
	const list = node?.relationship || node?.get?.('relationship');
	return Array.isArray(list) ? list : list ? [list] : [];
}

function ensureRelationshipsNode(modeler) {
	const model = modeler._model;
	const moddle = modeler._moddle;
	if (!model || !moddle) return null;
	let relationshipsNode = relationshipsNodeOf(model);
	if (!relationshipsNode) {
		relationshipsNode = moddle.create(ARCHIMATE_RELATIONSHIPS, {relationship: []});
		model.relationshipsNode = relationshipsNode;
		relationshipsNode.$parent = model;
	}
	return relationshipsNode;
}

function findElementById(model, elementId) {
	if (!model || !elementId) return null;
	const elementsNode = model.elementsNode || model.elements;
	const list = elementsNode?.element || elementsNode?.elements || [];
	const arr = Array.isArray(list) ? list : list ? [list] : [];
	return arr.find(e => getRefId(e) === elementId) ?? null;
}

function linkRelationshipEnds(relationship, srcElem, tgtElem) {
	if (srcElem) {
		if (!srcElem.outgoing) srcElem.outgoing = [];
		if (!srcElem.outgoing.includes(relationship)) srcElem.outgoing.push(relationship);
	}
	if (tgtElem) {
		if (!tgtElem.incoming) tgtElem.incoming = [];
		if (!tgtElem.incoming.includes(relationship)) tgtElem.incoming.push(relationship);
	}
}

export function findRelationshipById(model, relationshipId) {
	if (!relationshipId) return null;
	return listRelationships(model).find(r => getRefId(r) === relationshipId) ?? null;
}

/**
 * Кладёт в срез модели уже существующую связь каталога (без новой строки в relationships).
 * @param {import('../ModelerX6').default} modeler
 * @param {{identifier: string, name?: string, type?: string, source?: string, target?: string, accessType?: string}} dto
 * @returns {object | null}
 */
export function ensureRelationshipInModel(modeler, dto) {
	const model = modeler._model;
	const moddle = modeler._moddle;
	if (!model || !moddle || !dto?.identifier) return null;
	const existing = findRelationshipById(model, dto.identifier);
	if (existing) return existing;
	const srcElem = findElementById(model, dto.source);
	const tgtElem = findElementById(model, dto.target);
	if (!srcElem || !tgtElem) return null;
	const relationshipsNode = ensureRelationshipsNode(modeler);
	if (!relationshipsNode) return null;
	const relArr = listRelationships(model);
	const attrs = {
		id: dto.identifier,
		name: dto.name || '',
		type: dto.type || 'Association',
		source: srcElem,
		target: tgtElem,
	};
	if (dto.accessType) attrs.accessType = dto.accessType;
	const relationship = moddle.create(ARCHIMATE_RELATIONSHIP, attrs);
	if (moddle.ids?.claim) moddle.ids.claim(dto.identifier, relationship);
	relationship.$parent = relationshipsNode;
	relArr.push(relationship);
	if (relationshipsNode.set) relationshipsNode.set('relationship', relArr);
	else relationshipsNode.relationship = relArr;
	linkRelationshipEnds(relationship, srcElem, tgtElem);
	if (modeler._elementsById) modeler._elementsById[dto.identifier] = relationship;
	return relationship;
}

/**
 * View-connection для уже существующей catalog-связи. Новую relationship не создаёт.
 * @returns {{connectionId: string, relationshipRefId: string, sourceId: string, targetId: string, relationshipType: string, accessType?: string, label: string, bendpoints: Array} | {alreadyOnView: true} | {missingEndpoints: true} | null}
 */
export function placeExistingRelationship(modeler, relationship) {
	const moddle = modeler._moddle;
	const view = modeler._currentView;
	const relId = getRefId(relationship);
	if (!moddle || !view || !relId) return null;
	if (modeler._findViewElementByConceptId?.(relId, true)) {
		return {alreadyOnView: true};
	}
	const srcElemId = getRefId(relationship.source ?? relationship.$attrs?.source);
	const tgtElemId = getRefId(relationship.target ?? relationship.$attrs?.target);
	const sourceViewNode = srcElemId
		? modeler._findViewElementByConceptId?.(srcElemId, false)
		: null;
	const targetViewNode = tgtElemId
		? modeler._findViewElementByConceptId?.(tgtElemId, false)
		: null;
	if (!sourceViewNode || !targetViewNode) return {missingEndpoints: true};
	const sourceId = getRefId(sourceViewNode);
	const targetId = getRefId(targetViewNode);
	if (!sourceId || !targetId) return {missingEndpoints: true};
	const connectionId = moddle.ids?.nextPrefixed?.('id-conn-', view) ?? `id-conn-${Date.now()}`;
	const connObj = moddle.create('archimate:Connection', {id: connectionId});
	if (moddle.ids?.claim) moddle.ids.claim(connectionId, connObj);
	connObj.source = sourceViewNode;
	connObj.target = targetViewNode;
	connObj.relationshipRef = relationship;
	connObj.type = relationship.type || relationship.$attrs?.type || 'Association';
	connObj.$parent = view;
	if (!view.viewElements) view.viewElements = [];
	view.viewElements.push(connObj);
	const relationshipType = connObj.type || 'Association';
	const accessType = relationship.accessType ?? relationship.$attrs?.accessType ?? undefined;
	const label = relationship.name ?? relationship.$attrs?.name ?? '';
	return {
		connectionId,
		relationshipRefId: relId,
		sourceId,
		targetId,
		relationshipType,
		accessType,
		label: typeof label === 'string' ? label : String(label ?? ''),
		bendpoints: [],
	};
}

/**
 * Создаёт в модели relationship и view connection между двумя view-нодами.
 * @param {import('../ModelerX6').default} modeler
 * @param {Object} sourceViewNode — view-элемент узла-источника (должен иметь elementRef)
 * @param {Object} targetViewNode — view-элемент узла-цели
 * @param {string} [relationshipType] — Association, Flow, Access, …
 * @returns {{ connectionId: string, relationshipRefId: string, bendpoints: Array } | null}
 */
export function createConnection(
	modeler,
	sourceViewNode,
	targetViewNode,
	relationshipType = 'Association'
) {
	const model = modeler._model;
	const moddle = modeler._moddle;
	const view = modeler._currentView;
	if (!model || !moddle || !view) return null;

	const srcRef = sourceViewNode?.elementRef ?? sourceViewNode?.$attrs?.elementRef;
	const tgtRef = targetViewNode?.elementRef ?? targetViewNode?.$attrs?.elementRef;
	if (!srcRef || !tgtRef) return null;

	let relationshipsNode = model.relationshipsNode || model.relationships;
	if (!relationshipsNode) {
		relationshipsNode = moddle.create(ARCHIMATE_RELATIONSHIPS, {relationship: []});
		model.relationshipsNode = relationshipsNode;
		relationshipsNode.$parent = model;
	}

	const relList = relationshipsNode.relationship || relationshipsNode.get?.('relationship');
	const relArr = Array.isArray(relList) ? relList : relList ? [relList] : [];

	const relationship = moddle.create('archimate:Relationship', {
		type: relationshipType,
		source: srcRef,
		target: tgtRef,
	});
	if (moddle.ids?.nextPrefixed) {
		relationship.id = moddle.ids.nextPrefixed('id-rel-', relationship);
	} else {
		relationship.id = `id-rel-${Date.now()}`;
	}
	if (moddle.ids?.claim) moddle.ids.claim(relationship.id, relationship);
	relationship.$parent = relationshipsNode;
	relArr.push(relationship);
	if (relationshipsNode.set) relationshipsNode.set('relationship', relArr);
	else relationshipsNode.relationship = relArr;

	const connectionId = moddle.ids?.nextPrefixed?.('id-conn-', view) ?? `id-conn-${Date.now()}`;
	const connObj = moddle.create('archimate:Connection', {id: connectionId});
	if (moddle.ids?.claim) moddle.ids.claim(connectionId, connObj);
	connObj.source = sourceViewNode;
	connObj.target = targetViewNode;
	connObj.relationshipRef = relationship;
	connObj.$parent = view;
	if (!view.viewElements) view.viewElements = [];
	view.viewElements.push(connObj);

	return {
		connectionId,
		relationshipRefId: relationship.id,
		bendpoints: [],
	};
}

/**
 * Удаляет view connection и связанный relationship из модели. Edge с графа не удаляет.
 * @param {import('../ModelerX6').default} modeler
 * @param {string} connectionViewId — id view connection (и id edge на графе)
 */
export function removeConnection(modeler, connectionViewId) {
	const view = modeler._currentView;
	if (!view?.viewElements) return;

	const getElementId = o => o?.id ?? o?.$attrs?.id;
	const search = (arr, pred) => {
		for (let i = 0; i < arr.length; i++) {
			if (pred(arr[i])) return {item: arr[i], index: i, parent: arr};
			const ch = arr[i].viewElements || [];
			if (ch.length) {
				const found = search(ch, pred);
				if (found) return found;
			}
		}
		return null;
	};

	const found = search(view.viewElements, el => getElementId(el) === connectionViewId);
	if (found) {
		found.parent.splice(found.index, 1);
		const relRef = found.item.relationshipRef;
		const relId = relRef && (relRef.id ?? relRef.$attrs?.id);
		if (relId && modeler._model?.relationshipsNode?.relationship) {
			const rels = modeler._model.relationshipsNode.relationship;
			const relArr = Array.isArray(rels) ? rels : [rels].filter(Boolean);
			const idx = relArr.findIndex(r => (r?.id ?? r?.$attrs?.id) === relId);
			if (idx >= 0) relArr.splice(idx, 1);
		}
	}
}
